import React from 'react';
import { useTranslation } from 'react-i18next';
import { PackageSearch, Percent, ListChecks, Unlink } from 'lucide-react';
import { StatCard } from '../../../../ui/StatCard';
import { iconBgBlue, iconBgGreen, iconBgAmber, iconBgRed } from '../../../../../styling/stylingHelper';
import { eur } from '../../../../orderhistory/utils';
import { pctAbs } from '../common';

interface UtilizationStatCardsProps {
  potentialWasteEur: number;
  overOrderingRate: number;
  productsToReview: number;
  unmappedSpend: number;
  unmappedCount: number;
}

/**
 * The four headline KPIs for the Product Utilization report, rendered with the app's
 * shared `StatCard` (tinted 42×42 icon tile) so they match the dashboard KPI cards.
 * The first three follow the page's tab + shared filters (the caller sums them over
 * the scoped rows); `unmappedSpend` stays global because "purchased but not linked"
 * is outside that scope. Note `overOrderingRate` keeps the full period spend as its
 * denominator, so it reads as a share of total spend on every tab.
 */
export const UtilizationStatCards: React.FC<UtilizationStatCardsProps> = ({
  potentialWasteEur,
  overOrderingRate,
  productsToReview,
  unmappedSpend,
  unmappedCount,
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-4 mb-6">
      <StatCard
        icon={<PackageSearch className="w-6 h-6" style={{ color: '#2E90FA' }} />}
        iconBg={iconBgBlue}
        title={t('reports.utilization.kpi.potentialWaste')}
        value={eur(potentialWasteEur)}
        subtitle={t('reports.utilization.kpi.potentialWasteHint')}
        mobileLayout="tile"
      />
      <StatCard
        icon={<Percent className="w-6 h-6" style={{ color: '#17B26A' }} />}
        iconBg={iconBgGreen}
        title={t('reports.utilization.kpi.wastePercent')}
        value={pctAbs(overOrderingRate)}
        subtitle={t('reports.utilization.kpi.wastePercentHint')}
        mobileLayout="tile"
      />
      <StatCard
        icon={<ListChecks className="w-6 h-6" style={{ color: '#F79009' }} />}
        iconBg={iconBgAmber}
        title={t('reports.utilization.kpi.productsToReview')}
        value={String(productsToReview)}
        subtitle={t('reports.utilization.kpi.productsToReviewHint')}
        mobileLayout="tile"
      />
      <StatCard
        icon={<Unlink className="w-6 h-6" style={{ color: '#F04438' }} />}
        iconBg={iconBgRed}
        title={t('reports.utilization.kpi.unmappedSpend')}
        value={eur(unmappedSpend)}
        subtitle={t('reports.utilization.kpi.unmappedSpendHint', { count: unmappedCount })}
        mobileLayout="tile"
      />
    </div>
  );
};

/**
 * KPI row for the Not-linked tab. The mapped-row KPIs (potential waste, over-ordering
 * rate, products to review) are undefined for spend-only rows, so that tab gets its
 * own three instead of showing three zeroed cards.
 */
export const UnmappedStatCards: React.FC<{ spend: number; count: number; shareOfSpend: number }> = ({
  spend,
  count,
  shareOfSpend,
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
      <StatCard
        icon={<Unlink className="w-6 h-6" style={{ color: '#F04438' }} />}
        iconBg={iconBgRed}
        title={t('reports.utilization.kpi.unmappedSpend')}
        value={eur(spend)}
        subtitle={t('reports.utilization.kpi.unmappedSpendHint', { count })}
      />
      <StatCard
        icon={<PackageSearch className="w-6 h-6" style={{ color: '#2E90FA' }} />}
        iconBg={iconBgBlue}
        title={t('reports.utilization.kpi.unmappedCount')}
        value={String(count)}
        subtitle={t('reports.utilization.kpi.unmappedCountHint')}
      />
      <StatCard
        icon={<Percent className="w-6 h-6" style={{ color: '#F79009' }} />}
        iconBg={iconBgAmber}
        title={t('reports.utilization.kpi.unmappedShare')}
        value={pctAbs(shareOfSpend)}
        subtitle={t('reports.utilization.kpi.unmappedShareHint')}
      />
    </div>
  );
};

export default UtilizationStatCards;
